import { useEffect, useId, useState } from "react";
import { Icon } from "@keystar/ui/icon";
import { chevronDownIcon } from "@keystar/ui/icon/icons/chevronDownIcon";
import { chevronRightIcon } from "@keystar/ui/icon/icons/chevronRightIcon";
import { css, tokenSchema } from "@keystar/ui/style";
import { Text } from "@keystar/ui/typography";
import { useRouter } from "@keystone-6/core/admin-ui/router";
import type { ReactNode } from "react";

const STORAGE_PREFIX = "quota-admin-nav-group:";

function readPersistedExpanded(title: string): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_PREFIX + title);
    if (stored === "expanded") return true;
    if (stored === "collapsed") return false;
    return null;
  } catch {
    return null;
  }
}

function persistExpanded(title: string, expanded: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + title, expanded ? "expanded" : "collapsed");
  } catch {
    // A blocked localStorage loses persistence, never the nav itself.
  }
}

const headerStyles = css({
  alignItems: "center",
  appearance: "none",
  background: "transparent",
  border: 0,
  color: tokenSchema.color.foreground.neutralSecondary,
  cursor: "pointer",
  display: "flex",
  gap: tokenSchema.size.space.regular,
  paddingBlock: tokenSchema.size.space.regular,
  paddingInline: tokenSchema.size.space.medium,
  textAlign: "start",
  width: "100%",
  "&:hover": {
    color: tokenSchema.color.foreground.neutralEmphasis
  }
});

const titleStyles = css({
  textTransform: "uppercase"
});

// AC11: no list markers — items stay the same bare NavItem rows as the flat nav.
const groupStyles = css({
  listStyle: "none"
});

const panelStyles = css({
  listStyle: "none",
  margin: 0,
  padding: 0
});

type CollapsibleNavGroupProps = {
  /** Whether the current route belongs to one of this group's lists. */
  isActive: boolean;
  title: string;
  children: ReactNode;
};

/**
 * Hand-rolled disclosure for the Admin UI sidebar — @keystar/ui's NavGroup is
 * a static heading and the kit ships no collapsible primitive.
 */
export function CollapsibleNavGroup({ isActive, title, children }: CollapsibleNavGroupProps) {
  const { pathname } = useRouter();
  const panelId = useId();
  const [expanded, setExpanded] = useState(isActive);

  // On every route arrival: active-group expansion > persisted choice > collapsed.
  // Runs after hydration, so the persisted state never desyncs SSR markup.
  useEffect(() => {
    setExpanded(isActive || (readPersistedExpanded(title) ?? false));
  }, [isActive, pathname, title]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    persistExpanded(title, next);
  };

  return (
    <li className={groupStyles}>
      <button type="button" aria-expanded={expanded} aria-controls={panelId} onClick={toggle} className={headerStyles}>
        <Icon src={expanded ? chevronDownIcon : chevronRightIcon} size="small" />
        <Text size="small" weight="medium" color="inherit" UNSAFE_className={titleStyles}>
          {title}
        </Text>
      </button>
      <ul id={panelId} className={panelStyles}>
        {expanded ? children : null}
      </ul>
    </li>
  );
}
